<template>
  <div class="vap-page">
    <Loading
      :active.sync="isLoading"
      :is-full-page="false"
      loader="dots"
      :color="loaderColor"
      :background-color="loaderBackgroundColor"
    />
    <div class="vap-page-header">
      <h1>Email Settings</h1>
      <div class="btns-container">
        <VasionButton
          id="btnSave"
          class="last-btn"
          classProp="primary"
          @vasionButtonClicked="saveConfig"
        >
          Save
        </VasionButton>
      </div>
    </div>
    <div class="vap-page-content">
      <div>
        <div>
          <div class="bottom-buffer">
            <VasionInput
              id="send-email-from"
              v-model="sendEmailFrom"
              class="input-style"
              inputType="top-white"
              name="sendEmailFrom"
              :title="'Send Email From'"
            />
          </div>
          <div class="vasion-flex">
            <div class="right-buffer bottom-buffer medium-input">
              <VasionDropList
                id="email-provider"
                v-slot="slotItem"
                v-model="emailProvider"
                title="Email Provider"
                name="emailProvider"
                type="plain-list"
                :dataArray="emailProviderOptions"
                valueName="value"
                displayName="name"
                width="100%"
                @input="onEmailProviderChange"
              >
                {{ slotItem.item.name }}
              </VasionDropList>
            </div>
          </div>
          <!-- SMTP Settings -->
          <div v-if="emailProvider && emailProvider.value === 'smtp'">
            <div class="vasion-flex">
              <div class="right-buffer bottom-buffer medium-input">
                <VasionInput
                  id="smtp-server"
                  v-model="smtpServer"
                  class="input-style"
                  inputType="top-white"
                  name="smtpServer"
                  :title="'SMTP Server'"
                />
              </div>
              <div class="bottom-buffer medium-input">
                <VasionInput
                  id="smtp-port"
                  v-model="smtpPort"
                  class="input-style"
                  inputType="top-white"
                  name="smtpPort"
                  :title="'SMTP Port'"
                />
              </div>
            </div>
            <div class="vasion-flex">
              <div class="right-buffer bottom-buffer medium-input">
                <VasionInput
                  id="smtp-username"
                  v-model="smtpUsername"
                  class="input-style"
                  inputType="top-white"
                  name="smtpUsername"
                  :title="'SMTP Username'"
                />
              </div>
              <div class="bottom-buffer medium-input">
                <VasionInput
                  id="smtp-password"
                  v-model="smtpPassword"
                  class="input-style"
                  inputType="top-white"
                  name="smtpPassword"
                  specialType="password"
                  :title="'SMTP Password'"
                  :canToggleShowPassword="false"
                  preventPasswordAutofill
                  @blur="passwordInputOnBlur"
                  @focus="passwordInputOnFocus"
                />
              </div>
            </div>
            <div class="bottom-buffer-large">
              <VasionCheckbox
                id="smtpEnableSSL"
                name="smtpEnableSSL"
                :checked="smtpEnableSSL"
                @change="smtpEnableSSL = !smtpEnableSSL"
              >
                Enable SSL
              </VasionCheckbox>
            </div>
          </div>

          <!-- Office 365 Settings -->
          <div v-if="emailProvider && emailProvider.value === 'office365'">
            <div class="vasion-flex">
              <div class="right-buffer bottom-buffer medium-input">
                <VasionInput
                  id="office365-client-id"
                  v-model="office365ClientId"
                  class="input-style"
                  inputType="top-white"
                  name="office365ClientId"
                  :title="'Client ID'"
                />
              </div>
              <div class="bottom-buffer medium-input">
                <VasionInput
                  id="office365-tenant-id"
                  v-model="office365TenantId"
                  class="input-style"
                  inputType="top-white"
                  name="office365TenantId"
                  :title="'Tenant ID'"
                />
              </div>
            </div>
            <div class="vasion-flex">
              <div class="right-buffer bottom-buffer medium-input">
                <VasionInput
                  id="office365-client-secret"
                  v-model="office365ClientSecret"
                  class="input-style"
                  inputType="top-white"
                  name="office365ClientSecret"
                  specialType="password"
                  :title="'Client Secret'"
                  :canToggleShowPassword="false"
                  preventPasswordAutofill
                  @blur="office365PasswordInputOnBlur"
                  @focus="office365PasswordInputOnFocus"
                />
              </div>
              <div class="bottom-buffer medium-input">
                <VasionInput
                  id="office365-graph-url"
                  v-model="office365GraphUrl"
                  class="input-style"
                  inputType="top-white"
                  name="office365GraphUrl"
                  :title="'Graph URL'"
                />
              </div>
            </div>
          </div>
          <div class="bottom-buffer">
            <VasionInput
              id="max-merge-size"
              v-model="maxMergeSize"
              class="input-style"
              inputType="top-white"
              name="maxMergeSize"
              specialType="number"
              :title="'Max Merge and Mail Size (in bytes)'"
              :canToggleShowPassword="true"
            />
          </div>
          <div class="vasion-flex">
            <div class="bottom-buffer medium-input">
              <VasionCheckbox
                id="prompt-to-burn-annotations"
                name="promptToBurnAnnotations"
                :checked="promptToBurnAnnotations"
                @change="promptToBurnAnnotations = !promptToBurnAnnotations"
              >
                Prompt to Burn Annotations
              </VasionCheckbox>
            </div>
            <div class="bottom-buffer medium-input">
              <VasionCheckbox
                id="prompt-for-pdf-password"
                name="promptForPDFPassword"
                :checked="promptForPDFPassword"
                @change="promptForPDFPassword = !promptForPDFPassword"
              >
                Prompt for PDF Password
              </VasionCheckbox>
            </div>
          </div>
          <div class="bottom-buffer">
            <VasionDropList
              id="logoff-user-request-template"
              v-slot="slotItem"
              v-model="logoffUserRequestTemplate"
              title="Logoff User Request Template"
              name="logoffUserRequestTemplate"
              type="plain-list"
              :dataArray="templates"
              valueName="value"
              displayName="name"
              width="100%"
            >
              {{ slotItem.item.name }}
            </VasionDropList>
          </div>
          <div class="bottom-buffer">
            <VasionDropList
              v-slot="slotItem"
              v-model="documentLinkAccessUser"
              :dataArray="users"
              title="Document Link Default Access User"
              inputType="plain-list-search"
              valueName="value"
              displayName="name"
              width="100%"
              class="attribute-droplist"
              :openAbove="true"
              @input="isDirty = true"
            >
              {{ slotItem.item.name }}
            </VasionDropList>
          </div>
          <div>
            <VasionButton
              id="btnSendTestMessage"
              :classProp="'primary'"
              @vasionButtonClicked="sendTestMessage"
            >
              Send Test Message
            </VasionButton>
          </div>
        </div>
      </div>
    </div>
    <VasionSnackbar
      id="email-settings-snack"
      :showSnackbarBool.sync="showSnackbar"
      :snackbarImage="snackbarImage"
      :snackbarSubTitle="snackbarSubTitle"
      :snackbarTitle="snackbarTitle"
    />
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'

const PasswordPlaceholder = '790bbf9a16b84c6bb243'

export default {
  name: 'EmailSettings',
  components: {
    Loading,
  },
  data: function () {
    return {
      documentLinkAccessUser: 0,
      emailProvider: null,
      isLoading: false,
      loaderBackgroundColor: loaderBackgroundColor,
      loaderColor: loaderColor,
      logoffUserRequestTemplate: 0,
      maxMergeSize: 0,
      office365ClientId: '',
      office365ClientSecret: '',
      office365ClientSecretIsSet: false,
      office365GraphUrl: '',
      office365TenantId: '',
      promptToBurnAnnotations: false,
      promptForPDFPassword: false,
      sendEmailFrom: '',
      showSnackbar: false,
      showSnackbarBool: false,
      snackbarImage: false,
      snackbarSubTitle: '',
      snackbarTitle: '',
      smtpEnableSSL: true,
      smtpPassword: '',
      smtpPasswordIsSet: false,
      smtpPort: '',
      smtpServer: '',
      smtpUsername: '',
    }
  },
  computed: {
    emailProviderOptions() {
      return [
        { name: 'SMTP', value: 'smtp' },
        { name: 'Office 365', value: 'office365' }
      ]
    },
    templates() {
      const templateArray = this.$store.state.common.emailTemplates.map((t) => {
        return {
          name: t.sName,
          value: t.iID,
        }
      })
      templateArray.unshift({name: '', value: 0})
      return templateArray
    },
    users() {
      const userArray = this.$store.state.common.users
      userArray.unshift({name: '', value: 0})
      return userArray
    },
  },
  async created() {
    let settings
    [settings] = await Promise.all([
      this.$store.dispatch('admin/getEmailSettings'),
      this.$store.dispatch('common/getEmailTemplates'),
      this.$store.dispatch('common/getUsers'),
    ])
    this.sendEmailFrom = settings.sendEmailFrom
    this.smtpServer = settings.smtpServer
    this.smtpPort = settings.smtpPort
    this.smtpUsername = settings.smtpUsername
    this.smtpPassword = settings.smtpPasswordIsSet ? PasswordPlaceholder : ''
    this.smtpPasswordIsSet = settings.smtpPasswordIsSet
    this.smtpEnableSSL = settings.smtpEnableSSL
    this.maxMergeSize = settings.maxMergeSize
    this.promptToBurnAnnotations = settings.promptToBurnAnnotations
    this.promptForPDFPassword = settings.promptForPDFPassword
    this.logoffUserRequestTemplate = this.templates.find(x => x.value === settings.logoffUserRequestTemplate)
    this.documentLinkAccessUser = this.users.find(x => x.value === settings.documentLinkAccessUser)
    
    // Office 365 settings
    this.office365ClientId = settings.office365ClientId || ''
    this.office365TenantId = settings.office365TenantId || ''
    this.office365ClientSecret = settings.office365ClientSecretIsSet ? PasswordPlaceholder : ''
    this.office365ClientSecretIsSet = settings.office365ClientSecretIsSet || false
    this.office365GraphUrl = settings.office365GraphUrl || 'https://graph.microsoft.com'
    
    // Determine email provider based on existing settings - default to SMTP
    if (this.office365ClientId || this.office365TenantId || this.office365ClientSecretIsSet || (this.office365GraphUrl && this.office365GraphUrl !== 'https://graph.microsoft.com')) {
      this.emailProvider = this.emailProviderOptions.find(x => x.value === 'office365')
    } else {
      this.emailProvider = this.emailProviderOptions.find(x => x.value === 'smtp')
    }
  },
  methods: {
    buildPayload() {
      const payload = {
        sendEmailTechnique: 1,
        sendEmailFrom: this.sendEmailFrom,
        maxMergeSize: this.maxMergeSize,
        promptToBurnAnnotations: this.promptToBurnAnnotations,
        promptForPDFPassword: this.promptForPDFPassword,
        logoffUserRequestTemplate: this.logoffUserRequestTemplate?.value ? this.logoffUserRequestTemplate.value : 0,
        documentLinkAccessUser: this.documentLinkAccessUser?.value ? this.documentLinkAccessUser.value : 0,
      }

      if (this.emailProvider && this.emailProvider.value === 'smtp') {
        // SMTP settings
        payload.smtpServer = this.smtpServer
        payload.smtpPort = this.smtpPort
        payload.smtpUsername = this.smtpUsername
        payload.smtpPassword = this.smtpPassword === PasswordPlaceholder ? null : this.smtpPassword
        payload.smtpEnableSSL = this.smtpEnableSSL
        // Set Office 365 settings to null
        payload.office365ClientId = null
        payload.office365TenantId = null
        payload.office365ClientSecret = null
        payload.office365GraphUrl = null
      } else if (this.emailProvider && this.emailProvider.value === 'office365') {
        // Office 365 settings
        payload.sendEmailTechnique = 2
        payload.office365ClientId = this.office365ClientId
        payload.office365TenantId = this.office365TenantId
        payload.office365ClientSecret = this.office365ClientSecret === PasswordPlaceholder ? null : this.office365ClientSecret
        payload.office365GraphUrl = this.office365GraphUrl
        // Set SMTP settings to null
        payload.smtpServer = null
        payload.smtpPort = null
        payload.smtpUsername = null
        payload.smtpPassword = null
        payload.smtpEnableSSL = null
      }

      return payload
    },
    hideSnackbar() { this.showSnackbar = false },
    async saveConfig(showSnackbar = true) {
      if (!this.validateData()) {
        this.showSnackbar = true
        return false
      }

      const payload = this.buildPayload()
      this.isLoading = true
      try {
        const result = await this.$store.dispatch('admin/saveEmailSettings', payload)
        if (!result) {
          this.snackbarSubTitle = 'Failed to save settings'
          this.snackbarImage = false
          this.showSnackbar = true
          return false
        }
        this.snackbarSubTitle = result.Value ? result.Value : 'Settings saved'
        this.snackbarImage = !result.Value
        this.showSnackbar = showSnackbar || !!result.Value
        return result.Value ? false : true
      } catch {
        this.snackbarSubTitle = 'Failed to save settings'
        this.snackbarImage = false
        this.showSnackbar = true
        return false
      } finally {
        this.isLoading = false
      }
    },
    passwordInputOnBlur() {
      if (this.smtpPasswordIsSet && this.smtpPassword === '') {
        this.smtpPassword = PasswordPlaceholder
      }
    },
    passwordInputOnFocus() {
      if (this.smtpPassword === PasswordPlaceholder) {
        this.smtpPassword = ''
      }
    },
    office365PasswordInputOnBlur() {
      if (this.office365ClientSecretIsSet && this.office365ClientSecret === '') {
        this.office365ClientSecret = PasswordPlaceholder
      }
    },
    office365PasswordInputOnFocus() {
      if (this.office365ClientSecret === PasswordPlaceholder) {
        this.office365ClientSecret = ''
      }
    },
    onEmailProviderChange() {
      // Only set default Graph URL for Office 365 if it's empty, don't clear other values
      if (this.emailProvider && this.emailProvider.value === 'office365') {
        // Set default Graph URL for Office 365 if it's empty or default
        if (!this.office365GraphUrl || this.office365GraphUrl === '') {
          this.office365GraphUrl = 'https://graph.microsoft.com'
        }
      }
      // Values are preserved when switching providers and only cleared on save via buildPayload
    },
    async sendTestMessage() {
      if (!await this.saveConfig(false)) {
        return
      }
      this.showSnackbar = false
      this.snackbarSubTitle = 'Test message sent!'
      this.snackbarImage = true
      this.isLoading = true
      try {
        const response = await this.$store.dispatch('admin/sendTestEmail')
        if (!response || response.Value !== '') {
          this.snackbarSubTitle = response?.Value || 'Failed to send test message'
          this.snackbarImage = false
        }
      } catch {
        this.snackbarSubTitle = 'Failed to send test message'
        this.snackbarImage = false
      } finally {
        this.isLoading = false
        this.showSnackbar = true
      }
    },
    validateData() {
      this.snackbarSubTitle = ''
      this.snackbarImage = false

      if (!this.sendEmailFrom) {
        this.snackbarSubTitle = 'Please enter the Send Email From.'
        return false
      }

      if (!this.emailProvider) {
        this.snackbarSubTitle = 'Please select an Email Provider.'
        return false
      }

      if (this.emailProvider.value === 'smtp') {
        // SMTP validation
        if (!this.smtpServer) {
          this.snackbarSubTitle = 'Please enter the SMTP Server'
          return false
        }

        if (!this.smtpPort) {
          this.snackbarSubTitle = 'Please enter the SMTP Port'
          return false
        }

        if (!this.smtpUsername) {
          this.snackbarSubTitle = 'Please enter the SMTP Username'
          return false
        }

        if (!this.smtpPassword) {
          this.snackbarSubTitle = 'Please enter the SMTP Password'
          return false
        }
      } else if (this.emailProvider.value === 'office365') {
        // Office 365 validation
        if (!this.office365ClientId) {
          this.snackbarSubTitle = 'Please enter the Client ID'
          return false
        }

        if (!this.office365TenantId) {
          this.snackbarSubTitle = 'Please enter the Tenant ID'
          return false
        }

        if (!this.office365ClientSecret) {
          this.snackbarSubTitle = 'Please enter the Client Secret'
          return false
        }

        if (!this.office365GraphUrl) {
          this.snackbarSubTitle = 'Please enter the Graph URL'
          return false
        }
      }

      this.snackbarImage = false
      return true
    },
  },
}
</script>

<style lang="scss" scoped>
.bottom-buffer {
  padding-bottom: 15px;
}

.bottom-buffer-large {
  padding-bottom: 40px;
}

.right-buffer {
  padding-right: 20px;
}

.saveButtonDiv {
  text-align: right;
}

.mainDiv {
  background-color: white;
}

.title-div {
  margin: 20px;
}

.medium-input {
  width: 350px !important;
}
</style>
